import { AlertTriangle, Award, BellRing, CalendarCheck, CalendarClock, CalendarX, ClipboardCheck, Hand, Hourglass, Lightbulb, LifeBuoy, MessageSquareReply, MessageSquareText, Siren, Star, Target, TrendingUp, Users, type LucideIcon } from 'lucide-react'
import type { WorkerNotificationType } from '../index.js'

/** The picture and colour for each kind of worker notification, shared by the worker app and the operator's worker detail. */
const NOTIFICATION_ICON: Record<WorkerNotificationType, { Icon: LucideIcon; className: string }> = {
  streak_milestone: { Icon: Star, className: 'bg-gold/25 text-gold' },
  score_update: { Icon: TrendingUp, className: 'bg-marine/15 text-marine' },
  bid_window_approaching: { Icon: CalendarClock, className: 'bg-green/15 text-green' },
  coaching: { Icon: Lightbulb, className: 'bg-coral-lt text-coral' },
  tier_within_reach: { Icon: Target, className: 'bg-gold/25 text-gold' },
  tier_at_risk: { Icon: AlertTriangle, className: 'bg-coral-lt text-coral' },
  recovery: { Icon: LifeBuoy, className: 'bg-marine/15 text-marine' },
  improvement: { Icon: TrendingUp, className: 'bg-green/15 text-green' },
  custom_behavior_prompt: { Icon: ClipboardCheck, className: 'bg-marine/15 text-marine' },
  period_closing: { Icon: Hourglass, className: 'bg-gold/25 text-gold' },
  score_final: { Icon: Award, className: 'bg-marine/15 text-marine-dk' },
  welcome: { Icon: Hand, className: 'bg-green/15 text-green' },
  barrier_reply: { Icon: MessageSquareReply, className: 'bg-marine/15 text-marine-dk' },
  bridge_callout: { Icon: Siren, className: 'bg-coral-lt text-coral' },
  bridge_cancelled: { Icon: CalendarX, className: 'bg-marine/15 text-marine' },
  bridge_filled: { Icon: CalendarCheck, className: 'bg-green/15 text-green' },
  bridge_covered: { Icon: Users, className: 'bg-marine/15 text-marine' },
  bid_result: { Icon: CalendarCheck, className: 'bg-green/15 text-green' },
  swap_offer: { Icon: Users, className: 'bg-gold/30 text-ink' },
  open_shift_offer: { Icon: CalendarClock, className: 'bg-coral-lt text-coral' },
  open_shifts_posted: { Icon: CalendarClock, className: 'bg-marine/15 text-marine' },
  schedule_update: { Icon: CalendarCheck, className: 'bg-marine/15 text-marine' },
  schedule_reminder: { Icon: BellRing, className: 'bg-coral-lt text-coral' },
  bidding_open: { Icon: BellRing, className: 'bg-coral-lt text-coral' },
  low_census: { Icon: CalendarX, className: 'bg-gold/30 text-ink' },
  recall_offer: { Icon: CalendarClock, className: 'bg-green/15 text-green' },
  floated: { Icon: CalendarCheck, className: 'bg-marine/15 text-marine' },
  staff_message: { Icon: MessageSquareText, className: 'bg-marine/15 text-marine-dk' },
}

const SIZES = {
  sm: { badge: 'w-5 h-5', icon: 'w-3 h-3' },
  md: { badge: 'w-7 h-7', icon: 'w-4 h-4' },
} as const

export function NotificationIcon({ type, size }: { type: WorkerNotificationType; size: keyof typeof SIZES }) {
  const { Icon, className } = NOTIFICATION_ICON[type] ?? NOTIFICATION_ICON.score_update
  return (
    <div aria-hidden="true" className={`${SIZES[size].badge} rounded-full flex items-center justify-center shrink-0 ${className}`}>
      <Icon strokeWidth={2.25} className={SIZES[size].icon} />
    </div>
  )
}
